import { CreateElementParams, Geometry, HorizontalAlign, Log, LogGroup, Rect, SetStyles, Size, VerticalAlign, now } from "../.././MyHelpers";
import { Settings } from "../../Settings";
import { MyCanvas } from "../MyCanvas";
import { ISprite, SpriteUpdateState } from "./ISprite";
import { OnSpriteCompleteCallback, OnSpriteDrawFrameCallback, OnSpriteStartCallback } from "./Sprite";

export enum CanvasSpriteSizing{
	Original, // leave sprite original size
	Fit,
	Stretch
}

export interface ICanvasSpriteProperties
{
	clearOnlyPreviousDrawnRect: boolean;
}

export interface CanvasSpriteParams
{
	sprite: ISprite;
	canvasSize?: Size;
	drawFirstFrame?: boolean;
	sizing?: CanvasSpriteSizing;
	createElementParams?: CreateElementParams<"canvas">;
	clearOnComplete?: boolean;
}

const DefaultCanvasSpriteParams: CanvasSpriteParams =
{
	sprite: null,
	canvasSize: null,
	drawFirstFrame: true,
	sizing: CanvasSpriteSizing.Fit,
	createElementParams: null,
}

export class CanvasSprite
{
	public sprite: ISprite;
	public canvas: MyCanvas;

	protected canvasSize: Size;


	protected scalex: number = 1;
	protected scaley: number = 1;

	//public fitScale: number;

	public sizing: CanvasSpriteSizing = CanvasSpriteSizing.Fit;



	// draw first frame, so that image gets decoded
	protected drawFirstFrame: boolean = true;
	protected lastDrawRect: Rect = null;

	protected logGroup: LogGroup = null;

	protected properties: ICanvasSpriteProperties = 
	{
		clearOnlyPreviousDrawnRect: false
	};

	public ClearOnIdle: boolean = false;

	//public constructor(sprite: ISprite, canvasSize: Size = null, drawFirstFrame: boolean = true, sizing: CanvasSpriteSizing = CanvasSpriteSizing.Fit, params: CreateElementParams<"canvas"> = null)
	public constructor(canvasSpriteParams: CanvasSpriteParams)
	{

		let filledCanvasSpriteParams = {
			...DefaultCanvasSpriteParams,
			...canvasSpriteParams
		};

		this.ChangeSprite(filledCanvasSpriteParams.sprite);

		
		this.canvasSize = filledCanvasSpriteParams.canvasSize;

		if (this.canvasSize == null)
			this.canvasSize = filledCanvasSpriteParams.sprite.Size();



		if (!filledCanvasSpriteParams.createElementParams)
			filledCanvasSpriteParams.createElementParams = {};
		
		if (!filledCanvasSpriteParams.createElementParams.classes)
			filledCanvasSpriteParams.createElementParams.classes = [];

		filledCanvasSpriteParams.createElementParams.classes.push(this.sprite.ResourceIdentifier.ID());

		this.canvas = new MyCanvas(this.canvasSize, filledCanvasSpriteParams.createElementParams);
		
		this.drawFirstFrame = filledCanvasSpriteParams.drawFirstFrame;
		this.sizing = filledCanvasSpriteParams.sizing;

		SetStyles(this.canvas.view, {
			pointerEvents: "none",
			touchAction: "none",
		});

		//this.fitScale = 1; //Geometry.FitRectRatio(this.sprite.CurrentFrameRect(), this.canvas.rect,);
	}

	public View(): HTMLElement {
		return this.canvas.view;
	}

	public Start(repeat: boolean = true): void {
		this.sprite.Start(repeat);
		this.OnStart();
	}

	public Repeat(repeat: boolean)
	{
		this.sprite.Repeat(repeat);
	}

	public Restart(repeat: boolean = true): void {
		this.sprite.Restart(repeat);
	}

	public Reset(): void {
		this.sprite.Reset();
	}

	public Stop(): void {
		this.sprite.Stop();
	}

	public StopAndReset(): void {
		this.sprite.StopAndReset();
		this.Update();
	}

	public DrawFirstFrame(): void {
		this.drawFirstFrame = true;
	}

	public ChangeSprite(sprite: ISprite):void{
		if (!sprite) 
		{
			// Can't set to null sprite??
			debugger;
		}

		this.sprite = sprite;

		this.sprite.ClearOnCompleteCallback();
		this.sprite.AddOnCompleteCallback(()=> this.OnSpriteComplete());

		this.logGroup = sprite.LogGroup.Add("CanvasSprite");
	}

	public Size():Size
	{
		return this.canvas.size;
	}

	public GetDuration(): number{
		return this.sprite.Duration;
		//this.sprite.config.frames.length/ this.sprite.config.props.fps;
	}


	public Update(force: boolean = false): void {
		let updateState: SpriteUpdateState = this.sprite.Update(null);

		let contextLost: boolean = this.canvas.WasContextLost();
		if (contextLost)
		{
			console.log("Context lost")
		}

		let shouldUpdate = 
				updateState == SpriteUpdateState.FrameChanged ||
				this.drawFirstFrame || 
				force || 
				contextLost || 
				updateState == SpriteUpdateState.Finished;

		if (shouldUpdate)
		{
			let drawStartTime: number = now();

			// is idle and do we clear on idle?
			if (this.ClearOnIdle && (updateState == SpriteUpdateState.Finished || updateState == SpriteUpdateState.Idle))
			{
				// clear rect if required
				this.canvas.Clear();
				this.lastDrawRect = null;
			}
			else
			{
				let frameRectOriginal = this.sprite.CurrentFrameRect;
				let frameRect: Rect = this.canvas.rect;

				// stretch/fit
				if(this.sizing == CanvasSpriteSizing.Stretch)
				{
					frameRect = this.canvas.rect;
				}
				else if (this.sizing == CanvasSpriteSizing.Fit)
				{	
					//default is fit
					frameRect = Geometry.FitRect(frameRectOriginal, this.canvas.rect, HorizontalAlign.Center, VerticalAlign.Center);
				}
				else if (this.sizing == CanvasSpriteSizing.Original)
				{
					// leave as is
				}

				// scale after fitting
				if (this.scalex != 1 || this.scaley != 1)
				{
					frameRect = Geometry.ScaleRect(frameRect, this.scalex, this.scaley);
				}

				// clear
				if (this.properties.clearOnlyPreviousDrawnRect && this.lastDrawRect != null)
				{
					this.canvas.context.clearRect(this.lastDrawRect.x, this.lastDrawRect.y, this.lastDrawRect.width, this.lastDrawRect.height);
				}
				else
				{
					this.canvas.Clear();
				}


				this.canvas.DrawImage(this.sprite.CurrentFrame, this.sprite.CurrentFrameRect, frameRect);
				this.lastDrawRect = frameRect;
			}

			// // debug draw rect around image

			// this.canvas.context.strokeStyle = "red";
			// this.canvas.context.strokeRect(frameRect.x, frameRect.y, frameRect.width, frameRect.height);
			// this.canvas.context.restore();

			//if (Settings.SpriteFrameDebugging)

			this.drawFirstFrame = false;

			this.OnDrawFrame();

			let drawEndTime: number = now();

			let drawDuration: number = drawEndTime - drawStartTime;

			if (drawDuration > 1000 / 30)
			{
				Log.Error(this.logGroup, "Performance warning 30fps: sprite " + this.sprite.Name() + " frame " + this.sprite.CurrentFrameIndex + " took " + drawDuration.toFixed(3) + "ms, limit is " + (1000/30).toFixed(3) + "ms" )
			}
			else if (drawDuration > 1000 / 60)
			{
				Log.Error(this.logGroup, "Performance warning 60fps: sprite " + this.sprite.Name() + " frame " + this.sprite.CurrentFrameIndex + " took " + drawDuration.toFixed(3) + "ms, limit is " + (1000/60).toFixed(3) + "ms");
			}
			
			this.canvas.RestoreContext();
		}

		if (Settings.SpriteFrameDebugging)
		{
			this.canvas.context.save();

			this.canvas.context.textBaseline = "top";

			this.canvas.context.fillStyle = "white";
			this.canvas.context.fillRect(0, 0, 200, 50);

			// this.canvas.context.fillStyle = "black";
			// this.canvas.context.font = "70px Verdana";
			// this.canvas.context.fillText(this.sprite.CurrentFrameIndex.toString() + " s:" + updateState + " f: " + (this.drawFirstFrame ? 1 : 0), 0, 0);

			this.canvas.context.fillStyle = "red";
			this.canvas.context.font = "30px Verdana";
			this.canvas.context.fillText(this.sprite.CurrentFrameIndex.toString() + " s:" + updateState + " f: " + (this.drawFirstFrame ? 1 : 0), 5, 5);

			this.canvas.context.restore();
		}
	}

	public SetSize(size: Size): void {

		let roundedSize = new Size(Math.round(size.Width), Math.round(size.Height));

		if (roundedSize.Width != this.canvas.size.Width ||
			roundedSize.Height != this.canvas.size.Height)
		{
			this.canvas.SetSize(roundedSize);
		}
	}

	public Scale(scale: number): void {
		this.scalex = scale;
		this.scaley = scale;
	}

	public ScaleNonUniform(scalex: number, scaley: number)
	{
		this.scalex = scalex;
		this.scaley = scaley;
	}

	/*
	public AddOnCompleteCallback(callback: OnSpriteCompleteCallback): void {
		this.sprite.AddOnCompleteCallback(callback);
	}*/
	protected startCallbacks: OnSpriteStartCallback[] = [];
	protected OnStart(): void {
		for (let i = 0; i < this.startCallbacks.length; i++) {
			this.startCallbacks[i]();
		}
	}

	public AddOnStartCallback(callbacks: OnSpriteStartCallback): void {
		this.startCallbacks.push(callbacks);
	}


	private callbacks: OnSpriteCompleteCallback[] = [];
	public AddOnCompleteCallback(callback: OnSpriteCompleteCallback): void {
		this.callbacks.push(callback);
	}

	private OnSpriteComplete() {
		//// Log.Debug(this.logGroup, "Sprite complete " + this.callbacks.length);
		for (let i = 0; i < this.callbacks.length; i++) {
			this.callbacks[i]();
		}
	}

	protected drawCallbacks: OnSpriteDrawFrameCallback[] = [];
	public AddOnDrawFrameCallback(callback: OnSpriteDrawFrameCallback): void
	{
		this.drawCallbacks.push(callback);
	}

	protected OnDrawFrame(): void
	{
		for (let i = 0; i < this.drawCallbacks.length; i++)
		{
			this.drawCallbacks[i]();
		}
	}

	public Show()
	{
		this.canvas.view.style.visibility = "visible";
	}

	public Hide()
	{
		this.canvas.view.style.visibility = "hidden";
	}

	/** Returns content of the current canvas as an image url. */
	public PreviewImage(middleImage: boolean = false): string {
		

		let frameIndex: number = 0;
		if (middleImage)
		{
			frameIndex = Math.floor(this.sprite.NumFrames() / 2);			
		}

		let frameSize = this.sprite.GetDecodedFrameRect(frameIndex).Size();
		let canvas = new MyCanvas(frameSize);

		canvas.DrawImage(this.sprite.GetDecodedFrame(frameIndex), this.sprite.GetDecodedFrameRect(frameIndex), Rect.FromSize(frameSize)); 


		let dataCanvas = canvas.Canvas().toDataURL("image/png", 1.0);

		//console.log("ImageData", dataCanvas);
		return dataCanvas;
	}
}